<template>
  <div ref="ftsTableEdit" tabindex="0">
    <table class="fts-table-edit"> 
      <thead>
        <tr>
          <th class="table-row-action default-width" v-if="getTableActions"></th>
          <th :style="getColumnWidth(colItem)"
            v-for="colItem in fields"
            :key="colItem.id">
            <div class="col-label">
              <div class="col-label-name">{{getColumnLabel(colItem)}}</div>
              <i class="col-label-icon" :class="[getIcon(colItem.type), {'required-dot': colItem.required && !colItem.readOnly}]"></i>
            </div>
          </th>
        </tr>
      </thead>

      <draggable
        v-model="formData[item.fieldName]"
        v-bind="dragOptions"
        handle=".row-action-handle"
        @start="drag = true"
        @end="drag = false"
        tag="tbody"
      >
        <tr 
          :class="{'selected-row': selectedRow && selectedRow.id === colData.id }"
          v-for="(colData, colDataInd) in formData[item.fieldName]"
          :key="colData.id" 
          @click.capture="selectRow(colData)">
          <!-- actions -->
          <td class="table-row-action default-width" v-if="getTableActions">
            <div>
              <font-awesome-icon
                :icon="['fas', 'grip-vertical']"
                fixed-width
                class="row-action-handle"
                v-if="!selectedRow" />

              <font-awesome-icon
                :icon="['fas', 'plus']"
                fixed-width
                class="row-action-add"
                v-else
                @click.stop="addTableRow(colDataInd + 1)" />

              <font-awesome-icon
                :icon="['fas', 'trash']"
                fixed-width
                class="row-action-remove"
                @click.stop="removeTableRow(colData)" />
            </div>
          </td>
          <!-- fields -->
          <td 
            v-for="colItem in fields" 
            :key="colItem.id"
            :style="getColumnWidth(colItem)" 
            :class="{
              'col-error':  columnValidatorInvalid(colData.id, colItem.fieldName),
              'col-uploading': isFileQueue(colData.id),    
            }"
            @click="smartFocus(colItem, colData.id, colDataInd)">
            <!-- upload overlay -->
            <div class="upload-overlay" v-if="isFileQueue(colData.id) && isColumnUploading(colData.id, colItem.fieldName)">
              uploading
            </div>
            <!-- form display -->
            <fts-display class="form-display-borderless"
              :class="getElementClass(colItem)"
              :item="colItem"
              :form-data="colData"
              :vectorId="vectorId"
              :editMode="Boolean(selectedRow)"
              :ref="'formDisplayBorderless' + colItem.id + colData.id"
            />
            <!-- form edit -->
            <div class="form-element-edit form-element-borderless" 
              :ref="'formElementBorderless' + colItem.id + colData.id" 
              @click.stop.self="smartFocus(colItem, colData.id, colDataInd)" 
              @keyup.enter="nextRow(colItem, colData.id, colDataInd)"
              v-if="selectedRow && selectedRow.id === colData.id || getFileQueue(colData.id)"> 
              <!-- display -->
              <fts-display class="form-display-borderless"
                :item="colItem"
                :form-data="colData"
                :vectorId="vectorId"
                :class="getElementClass(colItem)"
                v-if="
                  colItem.display ||
                  colItem.readOnly ||
                  getColumnItemInput(colItem) === 'display' ||
                  (
                    (
                      colItem.type == 33 ||
                      getColumnItemInput(colItem) === 'link'
                    ) && isCustomLink(colData, colItem.fieldName)
                  )
                "
              />
              <!-- custom formatted text -->
              <cleave
                class="fts-form-control"
                v-model="colData[colItem.fieldName]"
                :options="colItem.options"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="getColumnItemInput(colItem) == 'formattedText'"
              />
              <!-- select (module) -->
              <fts-dropdown 
                :item="colItem" 
                :form-data="colData" 
                :taggable="true" 
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="(
                  colItem.type == 7 ||
                  colItem.type == 27 ||
                  getColumnItemInput(colItem) == 'dropDown' ||
                  getColumnItemInput(colItem) == 'select'
                ) && colItem.type != 20"
              />
              <!-- tag (multiple) -->
              <fts-dropdown
                :item="colItem"
                :form-data="colData"
                :multiple="true"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 28 || 
                  getColumnItemInput(colItem) == 'multipleDropDown'
                "
              />
              <!-- location -->
              <fts-location
                :item="colItem" 
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @close="columnValidatorTouch(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 9 ||
                  getColumnItemInput(colItem) == 'location'
                "
              />
              <!-- multiline text -->
              <fts-text-area
                class="fts-form-control"
                :item="colItem"
                :form-data="colData"
                :rows="1"
                @updateHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 16 ||
                  getColumnItemInput(colItem) == 'textArea'
                "
              />
              <!-- date time -->
              <fts-date-time
                :item="colItem"
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @close="columnValidatorTouch(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 5 ||
                  colItem.type == 6 ||
                  getColumnItemInput(colItem) == 'dateTime' ||
                  getColumnItemInput(colItem) == 'date' ||
                  getColumnItemInput(colItem) == 'time'
                "
              />
              <!-- integer -->
              <fts-integer
                :item="colItem"
                :form-data="colData"
                controls
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 2 ||
                  getColumnItemInput(colItem) == 'integer'
                " 
              />
              <!-- file upload -->
              <fts-file-upload 
                :form-data="colData"
                :item="colItem" 
                :vectorId="vectorId"
                @click.stop
                @onQueue="setFileQueue($event, colData.id, colItem.fieldName)"
                @input="columnOnInput(colData.id, colItem.fieldName)"          
                v-else-if="
                  colItem.type == 10 ||
                  colItem.type == 11 ||
                  colItem.type == 12 ||
                  colItem.type == 13 ||
                  getColumnItemInput(colItem) == 'imageUpload' ||
                  getColumnItemInput(colItem) == 'imagesUpload' ||
                  getColumnItemInput(colItem) == 'fileUpload' ||
                  getColumnItemInput(colItem) == 'filesUpload'
                " 
              />
              <!-- content -->
              <fts-content
                :item="colItem"
                :form-data="colData"
                :data="colData[colItem.fieldName]"
                @updateContentHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 22 ||
                  getColumnItemInput(colItem) == 'content'
                "
              />
              <!-- number / money -->
              <fts-number-currency
                class="fts-form-control number-currency"
                :item="colItem"
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 3 ||
                  colItem.type == 4 ||
                  getColumnItemInput(colItem) == 'number' ||
                  getColumnItemInput(colItem) == 'currency'
                "
              />
              <!-- yes no -->
              <checkbox
                v-model="colData[colItem.fieldName]"
                :color="'#279ec6'"
                :size="20"
                @change="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 1 ||
                  getColumnItemInput(colItem) == 'boolean' ||
                  getColumnItemInput(colItem) == 'toggle'
                "
              />
              <!-- profile (module) -->
              <fts-user
                :item="colItem"
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 20 ||
                  getColumnItemInput(colItem) == 'dropDownUser'
                "
              />
              <!-- phone number -->
              <fts-phone-number 
                :item="colItem" 
                :form-data="colData" 
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 26 ||
                  getColumnItemInput(colItem) == 'phone'
                "
              />
              <!-- signature -->
              <fts-signature
                :form-data="colData"
                :item="colItem"
                :vectorId="vectorId"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 29 ||
                  getColumnItemInput(colItem) == 'signature'
                "
              />
              <!-- default -->
              <input
                class="fts-form-control"
                type="text"
                autocomplete="off"
                v-model="colData[colItem.fieldName]"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else
              />
            </div>
          </td>
        </tr>
      </draggable>   
      <tr
        class="table-add-row"
        @click="addTableRow(formData[item.fieldName]? formData[item.fieldName].length : 0)"
        v-if="getTableActions"
      >
        <td :colspan="fields.length + 1">
          <div>
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width />add new
          </div>
        </td>
      </tr>
    </table>

    <div class="fts-table-form-action" v-if="getTableActions">
      <span v-if="selectedRow">
        Press
        <kbd>ESC</kbd> to unselect.
      </span>
      <span v-if="!selectedRow">
        <font-awesome-icon :icon="['fas', 'grip-vertical']" fixed-width />Click and drag to row to sort.
      </span>
      <span v-if="selectedRow">
        <font-awesome-icon :icon="['fas', 'trash']" fixed-width />Click and remove it.
      </span>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

import FTSDisplay from "./FTSDisplay";
import FTSDropdown from "./FTSDropdown";
import FTSTextArea from "./FTSTextArea";
import FTSDateTime from "./FTSDateTime";
import FTSInteger from "./FTSInteger";
import FTSLocation from "./FTSLocation";
import FTSFileUpload from "./FTSFileUpload";
import FTSContent from "./FTSContent";
import FTSUser from "./FTSUser";
import FTSNumberCurrency from "./FTSNumberCurrency";
import FTSPhoneNumber from "./FTSPhoneNumber";
import FTSSignature from "./FTSSignature";

import Checkbox from "vue-material-checkbox";

import commons from "../commons/commons";

export default {
  components: {
    draggable,
    "fts-display": FTSDisplay,
    "fts-dropdown": FTSDropdown,
    "fts-text-area": FTSTextArea,
    "fts-date-time": FTSDateTime,
    "fts-integer": FTSInteger,
    "fts-location": FTSLocation,
    "fts-file-upload": FTSFileUpload,
    "fts-content": FTSContent,
    "fts-user": FTSUser,
    "fts-number-currency": FTSNumberCurrency,
    "fts-phone-number": FTSPhoneNumber,
    "fts-signature": FTSSignature,
    checkbox: Checkbox,
  },
  props: {
    item: Object,
    formData: Object,
    vectorId: [Number, String],
    validator: Object
  },
  data: () => ({
    dragOptions: commons.dragOptions,
    selectedRow: null,
    fileQueues: [], 
  }),
  methods: {
    nextRow(colItem, colDataId, colDataInd) {
      // Testing
      // if(colItem.input === 'text' || colItem.input === 'number' || colItem.input === 'integer') {
      //   this.addTableRow(colDataInd + 1)
      //   this.smartFocus(colItem, this.selectedRow.id, colDataInd + 1)
      // }
    },
    addTableRow(index) {
      let newRow = {
        id: commons.newUUID()
      };

      this.item.fields.forEach(x => {
        if (x && x.fieldName.indexOf(".") === -1) {
          this.$set(newRow, x.fieldName, null);
        }
      });

      if (this.formData[this.item.fieldName] == null) {
        this.$set(this.formData, this.item.fieldName, []);
      }

      this.formData[this.item.fieldName].splice(index, 0, newRow);
      this.selectRow(newRow);
    },
    removeTableRow(item) {
      this.selectRow(null);
      let tableData = this.formData[this.item.fieldName];
      tableData.splice(tableData.indexOf(item), 1);
    },
    getElementClass(item) {
      if (item.input == 'boolean' || item.input == 'toggle' || item.type === 1) {
        return "te-yes-no-display"
      } else if (item.input == 'integer' || item.type === 2) {
        return "te-integer-display"
      } else if (item.input == 'number' || item.input == 'currency' || item.type === 3 || item.type === 4 ) {
        return "te-number-currency-display"
      } else if (item.input == 'signature' || item.type === 29) {
        return "te-signature-display"
      }
    },
    getColumnItemInput(item) {
      return item.input && !commons.isInteger(item.type) ? item.input : null;
    },
    getColumnWidth(item) {
      let minWidth = 150 //default minWidth
      if (item.columnWidth) {
        return {
          'width': item.columnWidth + "px",
          'minWidth': item.columnWidth + "px",
          'max-width': item.columnWidth + "px"
        }
        // minWidth = item.columnWidth
      } else if (item.display || item.input === 'display' || item.readOnly) {
        minWidth = 0
      } else if (
        item.type == 10 ||
        item.type == 11 ||
        item.type == 12 ||
        item.type == 13 ||
        item.input === 'imageUpload' || 
        item.input === 'imagesUpload' || 
        item.input === 'fileUpload' || 
        item.input === 'filesUpload'
      ) {
        minWidth = 250
      } else if (
        item.type == 26 ||
        item.input === 'phoneNumber'
      ) {
        minWidth = 200
      } else if (
        item.type == 1 ||
        item.input === 'boolean'
      ) {
        minWidth = 50
      } else if (
        item.type == 29 ||
        item.input === 'signature'
      ) {
        return {
          'width': '300px'
        } 
      }
      return "minWidth:" + minWidth + "px"
    },
    getColumnLabel(item) {
      return item.label || item.displayName;
    },
    getIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    setDisplayHeight(elHeight, itemId, colDataId, colDataInd) {
      let el = this.$refs['formDisplayBorderless' + itemId + colDataId];
      let itemIndexOf = 0;
      
      if (el && el.length > 1)
        itemIndexOf = colDataInd;

      el[itemIndexOf].$el.style.height = elHeight;
    },
    smartFocus(colItem, colDataId, colDataInd) {
      let element = this.$refs['formElementBorderless' + colItem.id + colDataId]
      let itemIndexOf = 0
      const colItemInput = this.getColumnItemInput(colItem);

      function setSelectionRange(el) {
        try {
          el.focus();
          if (el.type !== 'email' && el.type !== 'number') {
            const length = el.value.length;
            el.setSelectionRange(length, length);
          }
        } catch(err) {
          console.warn("[DOMException]", err);
        }
      }

      if (element && element.length > 1) {
        itemIndexOf = colDataInd
      }

      if (
        [0, 3, 4, 16, 25, 33].indexOf(colItem.type) > -1 ||
        ['text', 'number', 'textArea', 'email', 'link'].indexOf(colItemInput) > -1
      ) {
        const el = element[itemIndexOf].firstChild;
        setSelectionRange(el);
      } else if (
        [2, 5, 6, 7, 20, 27].indexOf(colItem.type) > -1 ||
        ['integer', 'date', 'dateTime', 'time', 'dropDown', 'dropDownUser', 'select'].indexOf(colItemInput) > -1
      ) {
        element[itemIndexOf].firstChild.getElementsByTagName("input")[0].focus()
      } else if (colItem.type === 22 || colItemInput === 'content') {
        element[itemIndexOf].firstChild.getElementsByClassName("ProseMirror")[0].focus()
      } else if (colItem.type === 26 || colItemInput === 'phoneNumber') {
        const el = element[itemIndexOf].getElementsByTagName("input")[1];
        setSelectionRange(el);
      } 
    },
    selectRow(item) {
      this.selectedRow = item;
    },
    // file queue
    setFileQueue(queue, colId, fieldName) {
      const uuid = `ftsFileUpload-${colId}-${fieldName}`;

      let findItem = this.fileQueues.find(x => x.id === uuid);
      if (findItem) {
        if (queue) {
          this.$set(findItem, 'queue', queue);
        } else {
          const findIndex = this.fileQueues.findIndex(x => x.id === uuid);
          if (findIndex > -1)
            this.fileQueues.splice(findIndex, 1);
        }
      } else if (queue) {
        this.fileQueues.push({
          id: uuid,
          colId: colId,
          fieldName: fieldName,
          queue: queue,
        });
      }
    },
    getFileQueue(colId) {
      return Array.isArray(this.fileQueues) && this.fileQueues.filter(x => x.colId === colId).length > 0;
    },
    isFileQueue(colId) {
      return (this.selectedRow === null || (this.selectedRow && this.selectedRow.id !== colId)) && this.getFileQueue(colId);
    },
    isColumnUploading(colId, fieldName) {
      return !!(Array.isArray(this.fileQueues) && this.fileQueues.find(x => x.colId === colId && x.fieldName === fieldName));
    },
    // custom link
    isCustomLink(colData, fieldName) {
      return Object.prototype.toString.call(colData[fieldName]) === '[object Object]';
    },
    // event listener
    escapeClick(e) {
      // Prevent Escape Click when FTSModal(show) is appendTo Body // Need Check got issue in quick view
      // let getFTSModal = document.body.getElementsByClassName('fts-modal')[0] 
      try {
        const el = this.$el.getElementsByTagName("table")[0] || this.$el;
        if (!el.contains(e.target) && this.selectedRow) {
          this.selectRow(null);
        }
      } catch(err) {
        console.warn(`[Vue warn][FTSTableEdit]: ${err}`)
      }
    },
    escapeHandler(e) {
			if (e.keyCode === 27) {   
        e.stopPropagation()
        this.selectRow(null);
      }
    },
    // validator
    columnValidator(rowId, colFieldName) {
      if (this.validator) {
        try {
          const validations = Object.values(this.validator.$each.$iter);
          return validations.find(x => x.$model.id == rowId)[colFieldName];
        } catch(err) { 
          console.error("TableEdit:ColumnValidator", err);
        }
      }
    },
    columnValidatorTouch(rowId, colFieldName) {
      let column = this.columnValidator(rowId, colFieldName);
      column && column.$touch();
    },
    columnValidatorInvalid(rowId, colFieldName) {
      const column = this.columnValidator(rowId, colFieldName);
      return column && column.$invalid && (column.$dirty || this.validator.$dirty);
    },
    // event
    columnOnInput(rowId, colFieldName) {
      this.columnValidatorTouch(rowId, colFieldName);
    },
    columnOnBlur(rowId, colFieldName) {
      this.columnValidatorTouch(rowId, colFieldName);
    },
  },
  computed: {
    getTableActions() {
      // hideTableActions => old
      if (this.item && (this.item.hideTableActions || this.item.disabledAddRemoveItems))
        return false;
      else 
        return true;
    },
    fields() {
      if(!this.item.fields && this.item.columns)
        this.item.fields = this.item.columns
    
      return this.item.fields.filter(x => x.input != 'hidden' && !x.hide && x.visible !== false)
    },
  },
  watch: {
    selectedRow(newVal, oldVal) {
      if (oldVal === null && newVal) {   
        document.addEventListener('click', this.escapeClick);
        this.$refs.ftsTableEdit.addEventListener('keydown', this.escapeHandler);   
      } else if (oldVal && newVal === null){
        document.removeEventListener('click', this.escapeClick);
        this.$refs.ftsTableEdit.removeEventListener('keydown', this.escapeHandler);  
        /* [disabled] due to safari unable compatible with preventScroll */ 
        // this.$refs.ftsTableEdit.focus({ preventScroll: true })
      }
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

td {
  &.col-error {
    @include theme() {
      background: rgba($color: theme-get('fts-danger'), $alpha: .1);
    }
  }
  &.col-uploading {
    .form-element-edit {
      display: none;
    }
  }
  .upload-overlay {
    @include flex-center-middle;
    position: absolute;
    height: 100%;
    width: 100%;
    @include theme() {
      background: rgba($color: theme-get('fts-gray-2'), $alpha: .75);
    }
    pointer-events: none;
    z-index: 1;
  }
}
</style>